import { Zap, SlidersHorizontal, Repeat, CalendarClock, MessagesSquare, Workflow } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Reveal, Overline } from "./Reveal";
import { IMAGES } from "@/config";

const CAPS = [
  { icon: Zap, title: "AI Lead Response", desc: "Automatically engage new leads the moment they contact your business." },
  { icon: SlidersHorizontal, title: "AI Qualification", desc: "Ask the right questions and determine whether prospects meet your criteria." },
  { icon: Repeat, title: "Automated Follow-Up", desc: "Continue conversations through SMS and email without manual outreach." },
  { icon: CalendarClock, title: "Appointment Booking", desc: "Guide qualified prospects toward scheduling calls and appointments." },
  { icon: MessagesSquare, title: "AI Customer Support", desc: "Answer common questions and route customers to the right next step." },
  { icon: Workflow, title: "Workflow Automation", desc: "Connect your systems and automate repetitive operational tasks." },
];

export default function AISection() {
  const navigate = useNavigate();
  return (
    <section id="ai" data-testid="ai-section" className="relative overflow-hidden border-t border-line px-6 py-28 md:px-12 md:py-40">
      <div className="absolute inset-0">
        <img src={IMAGES.ai} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-black/70" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1600px]">
        <Reveal><Overline>04 — AI &amp; Automation</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-8 max-w-4xl font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-white md:text-5xl lg:text-6xl">
            Your business shouldn&rsquo;t stop working <span className="text-zinc-500">when you do.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-400 md:text-lg">
            Leads wait too long for responses. Follow-up gets forgotten. Teams burn hours on
            repetitive tasks. TPA builds AI and automation systems that respond faster and
            operate around the clock.
          </p>
        </Reveal>

        <div className="mt-20 grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {CAPS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.06} className="h-full">
              <div
                data-testid={`ai-cap-${i + 1}`}
                className="beam-card h-full border border-transparent bg-black/50 p-8 backdrop-blur-sm transition-colors duration-500 hover:border-white/30"
              >
                <c.icon size={24} strokeWidth={1.5} className="text-crimson" />
                <h3 className="mt-6 font-display text-lg font-bold uppercase tracking-tight text-white">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <button
            data-testid="ai-explore-btn"
            onClick={() => navigate("/book")}
            className="mt-16 bg-white px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-black transition-colors duration-300 hover:bg-crimson hover:text-white"
          >
            Explore AI Solutions
          </button>
        </Reveal>
      </div>
    </section>
  );
}
